/**
 * Le pilote d'IA : ce qui fait enfin JOUER les huit niveaux.
 *
 * `niveaux.ts` sait quoi garder ; le moteur attend des actions (`LANCER`, `GARDER`,
 * `ARRETER`). Ce module fait la jonction, et c'est lui qui manquait pour que les
 * tables et la calibration décrivent un joueur réel plutôt qu'une intention.
 *
 * ## Trois pièges, et comment ils sont traités
 *
 * 1. **Garder les trois dés, c'est s'arrêter.** Émettre `GARDER([0,1,2])` puis
 *    `LANCER` relancerait zéro dé et ferait tourner la manche à vide. On émet
 *    `ARRETER`.
 * 2. **Ne pas redemander `GARDER` indéfiniment.** Le pilote compare la décision aux
 *    dés déjà marqués : s'ils correspondent, il ne reste qu'à lancer. Sans cette
 *    comparaison, une partie ne se termine jamais — d'où un test qui joue des
 *    parties entières plutôt que des coups isolés.
 * 3. **La décision porte sur des valeurs, l'action sur des index.** La traduction est
 *    faite par `gardeDuNiveau`, et c'est le seul endroit où un bug resterait muet :
 *    la décision serait juste et le dé gardé faux.
 *
 * Le moteur reste seul juge : ce module ne fait que proposer des actions, qu'il
 * refusera si elles sont illégales. Un test vérifie qu'il n'en propose jamais.
 */
import { evaluerMain } from "./combinaisons.js";
import { appliquer, lancersAutorises, ordreTour, terminee } from "./moteur.js";
import { gardeDuNiveau, profilDe, } from "./niveaux.js";
export class PasUneIA extends Error {
}
/**
 * Ce que l'IA sait au moment de décider, lu depuis l'état de la partie.
 *
 * `premier` vaut vrai tant que personne n'a fixé le nombre de lancers : c'est
 * exactement `lancersMax === null`, et c'est ce qui rend le sec possible. Le
 * confondre avec « je suis l'ouvreur » serait faux dès qu'un rampeau redistribue
 * l'ordre.
 */
export function contexteDepuisPartie(partie) {
    const manche = partie.manche;
    if (!manche) {
        return {
            phase: partie.phase,
            premier: true,
            scoresPoses: [],
            joueursApres: Math.max(0, partie.actifs.length - 1),
            joueursActifs: partie.actifs.length,
        };
    }
    const ordre = ordreTour(partie);
    const place = manche.tourDe === null ? -1 : ordre.indexOf(manche.tourDe);
    const scoresPoses = ordre
        .filter((siege) => manche.poses[siege] !== undefined)
        .map((siege) => evaluerMain(manche.poses[siege].main).score);
    return {
        phase: partie.phase,
        premier: manche.lancersMax === null,
        scoresPoses,
        joueursApres: place < 0 ? 0 : Math.max(0, ordre.length - place - 1),
        joueursActifs: partie.actifs.length,
    };
}
/** Relances encore permises au joueur courant : 1 ou 2, jamais 0 ici. */
function relancesRestantes(partie) {
    const manche = partie.manche;
    const utilises = manche.enCours?.lancers ?? 0;
    const reste = lancersAutorises(partie) - utilises;
    // Le pilote n'appelle ceci que lorsqu'une relance est possible ; on borne quand
    // même, parce que les tables ne connaissent que 1 et 2.
    return (reste >= 2 ? 2 : 1);
}
/**
 * Prochaine action de l'IA dont c'est le tour.
 *
 * `alea` est le tirage uniforme [0, 1[ qui décide de l'erreur ε. Il est **injecté**,
 * jamais tiré ici : c'est ce qui permet de rejouer une partie à l'identique et de
 * comparer au simulateur Python.
 */
export function actionSuivanteIA(partie, tables, alea = 1) {
    const manche = partie.manche;
    if (!manche || manche.tourDe === null) {
        throw new PasUneIA("aucun joueur ne doit agir");
    }
    const joueur = partie.joueurs[manche.tourDe];
    if (!joueur?.estIA) {
        throw new PasUneIA(`le siège ${manche.tourDe} n'est pas une IA`);
    }
    // Rien en main : il faut d'abord lancer. Vrai aussi dans un rampeau, où le jet est
    // unique et posé d'office — il n'y a rien à décider.
    if (!manche.enCours || manche.rampeau)
        return { type: "LANCER" };
    const des = manche.enCours.des;
    const main = des.map((d) => d.valeur);
    // Plus de relance disponible : on ne peut que poser.
    if (manche.enCours.lancers >= lancersAutorises(partie))
        return { type: "ARRETER" };
    const profil = profilDe(joueur.niveauIA);
    const voulus = gardeDuNiveau(profil, tables, main, relancesRestantes(partie), contexteDepuisPartie(partie), alea);
    // Garder les trois dés, c'est s'arrêter.
    if (voulus.length === 3)
        return { type: "ARRETER" };
    // Les dés sont-ils déjà marqués comme l'IA le veut ? Si oui, il ne reste qu'à
    // lancer. Sans ce test, le pilote redemanderait `GARDER` sans fin.
    const dejaGardes = des.filter((d) => d.garde).map((d) => d.id);
    const memeSelection = dejaGardes.length === voulus.length
        && dejaGardes.every((id) => voulus.includes(id));
    if (memeSelection)
        return { type: "LANCER" };
    return { type: "GARDER", des: voulus };
}
/**
 * Joue les actions de l'IA courante jusqu'à ce que ce ne soit plus son tour.
 *
 * Un tour d'IA vaut plusieurs actions (garder, puis lancer, puis s'arrêter), et
 * chacune produit une révision : c'est voulu, l'état reste sérialisable à chaque pas
 * et une reprise en cours de tour reste possible.
 */
export function jouerTourIA(partie, tables, tireur, alea, maxActions = 12) {
    let courante = partie;
    const siege = partie.manche?.tourDe;
    for (let i = 0; i < maxActions; i++) {
        const manche = courante.manche;
        if (!manche || manche.tourDe === null)
            break;
        if (!courante.joueurs[manche.tourDe]?.estIA)
            break;
        // Le tour a changé de main : c'est fini pour celui-ci.
        if (siege !== undefined && manche.tourDe !== siege && i > 0)
            break;
        const action = actionSuivanteIA(courante, tables, alea());
        courante = appliquer(courante, action, tireur);
        if (action.type === "ARRETER")
            break;
    }
    return courante;
}
/**
 * Fait jouer toutes les IA jusqu'à ce que la main revienne à un humain, ou que la
 * partie s'achève.
 *
 * C'est la fonction dont le solo a besoin (lot 4A) : l'humain agit, les IA
 * enchaînent, on rend la main. Elle ne joue jamais à la place d'un humain.
 */
export function jouerJusquAuJoueur(partie, tables, tireur, alea, maxTours = 2000) {
    let courante = partie;
    for (let i = 0; i < maxTours; i++) {
        if (terminee(courante))
            break;
        const manche = courante.manche;
        // `tourDe` nul hors partie terminée n'est pas un état d'attente : le moteur
        // résout la manche et prépare la suivante dans la même transition. S'il arrive
        // ici, il n'y a rien à pousser et insister ferait tourner la boucle à vide.
        if (!manche || manche.tourDe === null)
            break;
        if (!courante.joueurs[manche.tourDe]?.estIA)
            break; // à l'humain de jouer
        const avant = courante.revision;
        courante = jouerTourIA(courante, tables, tireur, alea);
        if (courante.revision === avant)
            break; // sécurité : aucun progrès
    }
    return courante;
}
//# sourceMappingURL=pilote.js.map